import React from "react";
import { motion } from "framer-motion";
import { InView, LineReveal } from "../motion/Reveal";
import { useLang, UI } from "@/lib/lang";

export default function Manifesto({ manifesto, stats = [] }) {
  const { t } = useLang();
  const chapters = manifesto?.chapters || [];
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute inset-0 grid-blueprint opacity-30" />
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] glow-blue opacity-60 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <p className="num-tick mb-4">{t(UI.section03)}</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]" data-testid="manifesto-title">
              <LineReveal text={t(manifesto?.title) || "How we work"} />
            </h2>
          </div>
        </div>

        <div className="space-y-0">
          {chapters.map((c, i) => (
            <Chapter key={c.num} chapter={c} isLast={i === chapters.length - 1} />
          ))}
        </div>

        {stats.length > 0 && (
          <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
            {stats.map((s, i) => (
              <InView
                key={i}
                delay={0.1 * i}
                className="border-b border-r border-white/10 last:border-r-0 md:last:border-r-0 p-6 md:p-10"
              >
                <div className="font-display text-[#1E7FFF] text-5xl md:text-7xl">
                  {s.num}
                </div>
                <div className="num-tick mt-3 text-white/60">{t(s.label)}</div>
              </InView>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Chapter({ chapter, isLast }) {
  const { t } = useLang();
  return (
    <InView y={40} className={`group grid grid-cols-12 gap-4 md:gap-6 items-start py-8 md:py-14 border-t border-white/10 ${isLast ? "border-b" : ""}`}>
      <div className="col-span-12 md:col-span-4">
        <motion.div
          initial={{ x: -60, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-mono-x text-[#1E7FFF] text-5xl md:text-7xl lg:text-8xl leading-none group-hover:translate-x-2 transition-transform duration-500"
          data-testid={`manifesto-chapter-${chapter.num}`}
        >
          {chapter.num}
        </motion.div>
      </div>
      <div className="col-span-12 md:col-span-6 md:col-start-6">
        <h3 className="font-display uppercase text-white text-2xl md:text-4xl lg:text-5xl leading-[1] mb-4">
          {t(chapter.title)}
        </h3>
        <p className="text-white/60 text-base md:text-lg font-body max-w-2xl leading-relaxed">
          {t(chapter.body)}
        </p>
      </div>
    </InView>
  );
}
